import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import '../../styles/raffle.css';

const API = process.env.REACT_APP_BACKEND_URL;

/** Hook that fetches the active raffle round (or returns null when none). */
export function useActiveRaffle() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/raffle/active`)
      .then(r => r.ok ? r.json() : { active: false })
      .then(d => { if (!cancelled) { setData(d && d.active ? d : null); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return { raffle: data, loading };
}

/** Sticky floating banner bottom-right. Hides for 24h once closed. */
export function RaffleFloater() {
  const { raffle } = useActiveRaffle();
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    const hidUntil = localStorage.getItem('raffle_floater_hidden_until');
    if (hidUntil && Date.now() < parseInt(hidUntil, 10)) {
      setClosed(true);
    }
  }, []);

  if (!raffle || closed) return null;

  const handleClose = (e) => {
    e.preventDefault();
    e.stopPropagation();
    localStorage.setItem('raffle_floater_hidden_until', String(Date.now() + 86400 * 1000));
    setClosed(true);
  };

  return (
    <Link to="/gewinnspiel" className="raffle-floater" data-testid="raffle-floater">
      <button className="rf-close" onClick={handleClose} aria-label="Schliessen" data-testid="raffle-floater-close">×</button>
      <span className="rf-emoji">🎁</span>
      <div className="rf-text">
        <div className="rf-title">Gewinne deine Bestellung!</div>
        <div className="rf-sub">Heiße Phase · Jetzt mitmachen →</div>
      </div>
    </Link>
  );
}

/** Bigger banner used on the homepage hero. */
// Rotating mystery taglines instead of exact numbers - creates urgency without exposing low counts
const RAFFLE_TAGLINES = [
  { icon: '🔥', text: 'Heiße Phase – jetzt einsteigen!' },
  { icon: '⏳', text: 'Gleich wird gezogen – sei dabei!' },
  { icon: '🎯', text: 'Jede Bestellung ist ein Los' },
  { icon: '✨', text: 'Soeben hat jemand mitgemacht' },
  { icon: '🚀', text: 'Glücksphase aktiv' },
  { icon: '🎁', text: 'Trostpreis garantiert – alle gewinnen' },
];

const TICKET_TAGLINES = [
  { icon: '🔥', text: 'Heiße Phase – die letzten Lose gehen schnell' },
  { icon: '⏳', text: 'Soeben wurden weitere Lose verkauft' },
  { icon: '🚀', text: 'Sichere dir deinen Platz in der Lostrommel' },
  { icon: '🎯', text: 'Heute mit Glück das Auto deines Lebens' },
  { icon: '✨', text: 'Verkauf laeuft – jeder Tag zaehlt' },
  { icon: '🎁', text: 'Ab nur 2 EUR pro Los dabei sein' },
];

function useRotatingTagline(pool, intervalMs = 4500) {
  const [idx, setIdx] = useState(() => Math.floor(Math.random() * pool.length));
  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % pool.length), intervalMs);
    return () => clearInterval(t);
  }, [pool, intervalMs]);
  return pool[idx];
}

export function RaffleHeroStrip() {
  const { raffle } = useActiveRaffle();
  const tagline = useRotatingTagline(RAFFLE_TAGLINES);
  if (!raffle) return null;
  return (
    <div className="raffle-hero-strip" data-testid="raffle-hero-strip">
      <div style={{ flex: 1, minWidth: 220, position: 'relative' }}>
        <span className="rh-tag">Gewinnspiel</span>
        <h2>Gewinne deine Bestellung zurück!</h2>
        <p>Jede Bestellung nimmt automatisch teil. Verlieren ausgeschlossen — alle anderen bekommen einen 5 € Gutschein!</p>
      </div>
      <div className="rh-mystery" data-testid="raffle-mystery">
        <div className="rh-mystery-icon">{tagline.icon}</div>
        <div className="rh-mystery-text">{tagline.text}</div>
      </div>
      <Link to="/gewinnspiel" className="rh-cta" data-testid="raffle-cta">Mehr erfahren</Link>
    </div>
  );
}

/** Post-order success modal "Du bist dabei!" */
export function RaffleSuccessModal({ raffleSlug, onClose }) {
  const { raffle } = useActiveRaffle();
  if (!raffleSlug) return null;
  return (
    <div className="raffle-success-modal" data-testid="raffle-success-modal" onClick={onClose}>
      <div className="rsm-card" onClick={e => e.stopPropagation()}>
        <button className="rsm-close" onClick={onClose} aria-label="Schliessen">×</button>
        <span className="rsm-emoji">🎉</span>
        <h3>Du nimmst automatisch teil!</h3>
        <p>
          Deine Bestellung ist in der Lostrommel für <strong>{raffle?.title || 'unser aktuelles Gewinnspiel'}</strong>.
        </p>
        <p style={{ marginTop: 12 }}>
          Wenn du gewinnst, bekommst du deine komplette Bestellung erstattet.<br/>
          <strong>Verlieren ausgeschlossen:</strong> Alle anderen Teilnehmer erhalten einen 5 € Trostpreis-Gutschein.
        </p>
        <Link to="/gewinnspiel" style={{
          display: 'inline-block',
          marginTop: 18,
          background: '#EC2229',
          color: '#fff',
          padding: '10px 24px',
          borderRadius: 8,
          fontWeight: 600,
          textDecoration: 'none',
        }}>So funktioniert's →</Link>
      </div>
    </div>
  );
}
